import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Settings, Plus, Pencil, Trash2, Users2, BookOpen, ShieldAlert, Megaphone, Store, Check } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const blankCourse = {
  title: "", format: "Individual", level: "All levels", focus: "Foundations", duration: "", sessions: "",
  price: "", image: "", spots: 6, next_start: "Rolling enrolment", description: "", highlights: "",
  instructor_name: "", instructor_role: "", instructor_bio: "",
};
const blankAd = { title: "", description: "", image: "", link_url: "", advertiser: "", placement: "dashboard", active: true };
const blankListing = { name: "", category: "Trainer", description: "", location: "", website: "", phone: "", image: "", featured: false, status: "approved" };

export default function Manage() {
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [ads, setAds] = useState([]);
  const [listings, setListings] = useState([]);
  const [cats, setCats] = useState([]);

  // dialogs
  const [courseOpen, setCourseOpen] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [cf, setCf] = useState(blankCourse);
  const [adOpen, setAdOpen] = useState(false);
  const [editingAd, setEditingAd] = useState(null);
  const [af, setAf] = useState(blankAd);
  const [listOpen, setListOpen] = useState(false);
  const [editingList, setEditingList] = useState(null);
  const [lf, setLf] = useState(blankListing);

  const load = () => {
    api.get("/courses").then((r) => setCourses(r.data));
    api.get("/enrollments").then((r) => setEnrollments(r.data)).catch(() => {});
    api.get("/ads/all").then((r) => setAds(r.data)).catch(() => {});
    api.get("/listings/all").then((r) => setListings(r.data)).catch(() => {});
    api.get("/directory/categories").then((r) => setCats(r.data)).catch(() => {});
  };
  useEffect(() => { if (user?.is_admin) load(); }, [user]); // eslint-disable-line

  // ----- courses -----
  const openNewCourse = () => { setEditingCourse(null); setCf(blankCourse); setCourseOpen(true); };
  const openEditCourse = (c) => {
    setEditingCourse(c);
    setCf({ ...blankCourse, ...c, highlights: (c.highlights || []).join(", "), instructor_name: c.instructor?.name || "", instructor_role: c.instructor?.role || "", instructor_bio: c.instructor?.bio || "" });
    setCourseOpen(true);
  };
  const saveCourse = async () => {
    if (!cf.title.trim() || !cf.instructor_name.trim()) { toast.error("Course title and trainer name required"); return; }
    const payload = {
      title: cf.title, format: cf.format, level: cf.level, focus: cf.focus, duration: cf.duration, sessions: cf.sessions,
      price: cf.price, image: cf.image, spots: Number(cf.spots) || 0, next_start: cf.next_start, description: cf.description,
      highlights: (cf.highlights || "").split(",").map((s) => s.trim()).filter(Boolean),
      instructor: { name: cf.instructor_name, role: cf.instructor_role, bio: cf.instructor_bio },
    };
    try {
      if (editingCourse) await api.put(`/courses/${editingCourse.id}`, payload); else await api.post("/courses", payload);
      toast.success("Saved"); setCourseOpen(false); load();
    } catch { toast.error("Save failed"); }
  };
  const delCourse = async (id) => { if (window.confirm("Delete this course?")) { await api.delete(`/courses/${id}`); load(); } };

  // ----- ads -----
  const openNewAd = () => { setEditingAd(null); setAf(blankAd); setAdOpen(true); };
  const openEditAd = (a) => { setEditingAd(a); setAf({ ...blankAd, ...a }); setAdOpen(true); };
  const saveAd = async () => {
    if (!af.title.trim()) { toast.error("Ad title required"); return; }
    try {
      if (editingAd) await api.put(`/ads/${editingAd.id}`, af); else await api.post("/ads", af);
      toast.success("Saved"); setAdOpen(false); load();
    } catch { toast.error("Save failed"); }
  };
  const delAd = async (id) => { if (window.confirm("Delete this ad?")) { await api.delete(`/ads/${id}`); load(); } };

  // ----- listings -----
  const openNewList = () => { setEditingList(null); setLf(blankListing); setListOpen(true); };
  const openEditList = (l) => { setEditingList(l); setLf({ ...blankListing, ...l }); setListOpen(true); };
  const saveList = async () => {
    if (!lf.name.trim()) { toast.error("Business name required"); return; }
    try {
      if (editingList) await api.put(`/listings/${editingList.id}`, lf); else await api.post("/listings", lf);
      toast.success("Saved"); setListOpen(false); load();
    } catch { toast.error("Save failed"); }
  };
  const approveList = async (l) => { await api.put(`/listings/${l.id}`, { ...l, status: "approved" }); toast.success("Approved"); load(); };
  const delList = async (id) => { if (window.confirm("Delete this listing?")) { await api.delete(`/listings/${id}`); load(); } };

  const scf = (k) => (e) => setCf({ ...cf, [k]: e.target.value });
  const saf = (k) => (e) => setAf({ ...af, [k]: e.target.value });
  const slf = (k) => (e) => setLf({ ...lf, [k]: e.target.value });

  const pendingCount = listings.filter((l) => l.status === "pending").length;

  if (!user?.is_admin) {
    return (
      <div data-testid="manage-page" className="max-w-md mx-auto mt-16 text-center">
        <div className="rounded-2xl bg-card border border-border/70 p-8 shadow-sm">
          <div className="h-12 w-12 rounded-2xl bg-destructive/10 flex items-center justify-center mx-auto mb-4"><ShieldAlert className="h-6 w-6 text-destructive" /></div>
          <h1 className="font-head text-2xl font-bold">Admins only</h1>
          <p className="text-muted-foreground text-sm mt-1">This area is restricted to the account owner.</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="manage-page">
      <div className="flex items-center gap-3 mb-2">
        <div className="h-11 w-11 rounded-2xl bg-primary/15 flex items-center justify-center"><Settings className="h-6 w-6 text-primary" /></div>
        <h1 className="font-head text-4xl font-extrabold tracking-tight">Manage</h1>
      </div>
      <p className="text-muted-foreground mb-6 max-w-2xl">Trainers & courses, advertisements, the business directory, and enrolments.</p>

      <Tabs defaultValue="courses">
        <TabsList className="bg-muted/60 rounded-xl p-1 flex-wrap h-auto">
          <TabsTrigger value="courses" data-testid="tab-manage-courses" className="rounded-lg"><BookOpen className="h-4 w-4 mr-1.5" />Courses</TabsTrigger>
          <TabsTrigger value="ads" data-testid="tab-manage-ads" className="rounded-lg"><Megaphone className="h-4 w-4 mr-1.5" />Ads ({ads.length})</TabsTrigger>
          <TabsTrigger value="directory" data-testid="tab-manage-directory" className="rounded-lg"><Store className="h-4 w-4 mr-1.5" />Directory{pendingCount ? ` · ${pendingCount} new` : ""}</TabsTrigger>
          <TabsTrigger value="subs" data-testid="tab-manage-subs" className="rounded-lg"><Users2 className="h-4 w-4 mr-1.5" />Subscribers ({enrollments.length})</TabsTrigger>
        </TabsList>

        {/* Courses */}
        <TabsContent value="courses" className="mt-6">
          <div className="flex justify-end mb-4"><Button data-testid="add-course" onClick={openNewCourse} className="rounded-full"><Plus className="h-4 w-4 mr-1" /> Add trainer / course</Button></div>
          <div className="grid gap-3">
            {courses.map((c) => (
              <div key={c.id} data-testid={`manage-course-${c.id}`} className="rounded-2xl bg-card border border-border/70 p-4 flex items-center gap-4 shadow-sm">
                <div className="h-14 w-14 rounded-xl overflow-hidden bg-muted shrink-0">{c.image && <img src={c.image} alt="" className="h-full w-full object-cover" />}</div>
                <div className="min-w-0 flex-1">
                  <div className="font-head font-semibold truncate">{c.title}</div>
                  <div className="text-sm text-muted-foreground truncate">{c.instructor?.name} · {c.instructor?.role}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{c.format} · {c.focus} · {c.price}</div>
                </div>
                <button data-testid={`edit-course-${c.id}`} onClick={() => openEditCourse(c)} className="p-2 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground"><Pencil className="h-4 w-4" /></button>
                <button data-testid={`del-course-${c.id}`} onClick={() => delCourse(c.id)} className="p-2 rounded-lg hover:bg-destructive/10 text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* Ads */}
        <TabsContent value="ads" className="mt-6">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-muted-foreground">Banner ads shown to users (free for now).</p>
            <Button data-testid="add-ad" onClick={openNewAd} className="rounded-full"><Plus className="h-4 w-4 mr-1" /> Add ad</Button>
          </div>
          {ads.length === 0 ? <div className="rounded-2xl border border-dashed border-border p-10 text-center text-muted-foreground">No ads yet.</div> : (
            <div className="grid gap-3">
              {ads.map((a) => (
                <div key={a.id} data-testid={`manage-ad-${a.id}`} className="rounded-2xl bg-card border border-border/70 p-4 flex items-center gap-4 shadow-sm">
                  <div className="h-12 w-12 rounded-xl overflow-hidden bg-muted shrink-0">{a.image && <img src={a.image} alt="" className="h-full w-full object-cover" />}</div>
                  <div className="min-w-0 flex-1">
                    <div className="font-head font-semibold truncate">{a.title} {!a.active && <span className="text-xs text-muted-foreground">(inactive)</span>}</div>
                    <div className="text-xs text-muted-foreground truncate">{a.advertiser} · {a.placement}</div>
                  </div>
                  <button data-testid={`edit-ad-${a.id}`} onClick={() => openEditAd(a)} className="p-2 rounded-lg hover:bg-muted text-muted-foreground"><Pencil className="h-4 w-4" /></button>
                  <button data-testid={`del-ad-${a.id}`} onClick={() => delAd(a.id)} className="p-2 rounded-lg hover:bg-destructive/10 text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Directory */}
        <TabsContent value="directory" className="mt-6">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-muted-foreground">Approve business submissions & manage listings.</p>
            <Button data-testid="add-listing" onClick={openNewList} className="rounded-full"><Plus className="h-4 w-4 mr-1" /> Add listing</Button>
          </div>
          <div className="grid gap-3">
            {listings.map((l) => (
              <div key={l.id} data-testid={`manage-listing-${l.id}`} className="rounded-2xl bg-card border border-border/70 p-4 flex items-center gap-4 shadow-sm">
                <div className="h-12 w-12 rounded-xl overflow-hidden bg-muted shrink-0">{l.image && <img src={l.image} alt="" className="h-full w-full object-cover" />}</div>
                <div className="min-w-0 flex-1">
                  <div className="font-head font-semibold truncate">{l.name} {l.featured && <span className="text-xs text-secondary">★</span>}</div>
                  <div className="text-xs text-muted-foreground truncate">{l.category} · {l.location || "—"}</div>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full ${l.status === "approved" ? "bg-primary/15 text-primary" : "bg-secondary/15 text-secondary"}`}>{l.status}</span>
                {l.status === "pending" && <button data-testid={`approve-listing-${l.id}`} onClick={() => approveList(l)} className="p-2 rounded-lg hover:bg-primary/10 text-primary"><Check className="h-4 w-4" /></button>}
                <button data-testid={`edit-listing-${l.id}`} onClick={() => openEditList(l)} className="p-2 rounded-lg hover:bg-muted text-muted-foreground"><Pencil className="h-4 w-4" /></button>
                <button data-testid={`del-listing-${l.id}`} onClick={() => delList(l.id)} className="p-2 rounded-lg hover:bg-destructive/10 text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
            {listings.length === 0 && <div className="rounded-2xl border border-dashed border-border p-10 text-center text-muted-foreground">No listings yet.</div>}
          </div>
        </TabsContent>

        {/* Subscribers */}
        <TabsContent value="subs" className="mt-6">
          {enrollments.length === 0 ? <div className="rounded-2xl border border-dashed border-border p-12 text-center bg-card/50 text-muted-foreground">No enrolments yet.</div> : (
            <div className="grid gap-3">
              {enrollments.map((e) => (
                <div key={e.id} className="rounded-2xl bg-card border border-border/70 p-4 shadow-sm">
                  <div className="flex items-center justify-between"><div className="font-medium">{e.name || "Member"} <span className="text-sm text-muted-foreground">· {e.email}</span></div><span className="text-xs bg-primary/15 text-primary px-2 py-0.5 rounded-full">{e.status}</span></div>
                  <div className="text-sm text-muted-foreground mt-1">Course: <b>{e.course_slug}</b></div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Course dialog */}
      <Dialog open={courseOpen} onOpenChange={setCourseOpen}>
        <DialogContent className="max-w-lg rounded-2xl max-h-[88vh] overflow-y-auto scroll-thin">
          <DialogHeader><DialogTitle className="font-head">{editingCourse ? "Edit course" : "Add trainer / course"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Course title *</Label><Input data-testid="c-title" value={cf.title} onChange={scf("title")} /></div>
            <div><Label>Format</Label><select data-testid="c-format" value={cf.format} onChange={scf("format")} className="w-full mt-1 rounded-md border border-input bg-background px-3 py-2 text-sm"><option>Individual</option><option>Group</option></select></div>
            <div><Label>Focus</Label><Input data-testid="c-focus" value={cf.focus} onChange={scf("focus")} /></div>
            <div><Label>Level</Label><Input data-testid="c-level" value={cf.level} onChange={scf("level")} /></div>
            <div><Label>Price</Label><Input data-testid="c-price" value={cf.price} onChange={scf("price")} placeholder="$200" /></div>
            <div><Label>Duration</Label><Input data-testid="c-duration" value={cf.duration} onChange={scf("duration")} /></div>
            <div><Label>Spots</Label><Input data-testid="c-spots" type="number" value={cf.spots} onChange={scf("spots")} /></div>
            <div className="col-span-2"><Label>Sessions</Label><Input data-testid="c-sessions" value={cf.sessions} onChange={scf("sessions")} /></div>
            <div className="col-span-2"><Label>Next start</Label><Input data-testid="c-start" value={cf.next_start} onChange={scf("next_start")} /></div>
            <div className="col-span-2"><Label>Image URL</Label><Input data-testid="c-image" value={cf.image} onChange={scf("image")} /></div>
            <div className="col-span-2"><Label>Description</Label><Textarea data-testid="c-desc" value={cf.description} onChange={scf("description")} /></div>
            <div className="col-span-2"><Label>Highlights (comma separated)</Label><Input data-testid="c-highlights" value={cf.highlights} onChange={scf("highlights")} /></div>
            <div className="col-span-2 border-t border-border pt-2 mt-1"><Label className="font-semibold">Trainer / professional</Label></div>
            <div className="col-span-2"><Label>Name *</Label><Input data-testid="c-inst-name" value={cf.instructor_name} onChange={scf("instructor_name")} /></div>
            <div className="col-span-2"><Label>Role / credentials</Label><Input data-testid="c-inst-role" value={cf.instructor_role} onChange={scf("instructor_role")} /></div>
            <div className="col-span-2"><Label>Bio</Label><Textarea data-testid="c-inst-bio" value={cf.instructor_bio} onChange={scf("instructor_bio")} /></div>
          </div>
          <div className="flex justify-end gap-2 pt-2"><Button variant="ghost" onClick={() => setCourseOpen(false)}>Cancel</Button><Button data-testid="save-course" onClick={saveCourse} className="rounded-full">Save</Button></div>
        </DialogContent>
      </Dialog>

      {/* Ad dialog */}
      <Dialog open={adOpen} onOpenChange={setAdOpen}>
        <DialogContent className="max-w-lg rounded-2xl">
          <DialogHeader><DialogTitle className="font-head">{editingAd ? "Edit ad" : "Add advertisement"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Title *</Label><Input data-testid="ad-title" value={af.title} onChange={saf("title")} /></div>
            <div><Label>Description</Label><Textarea data-testid="ad-desc" value={af.description} onChange={saf("description")} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Advertiser</Label><Input data-testid="ad-advertiser" value={af.advertiser} onChange={saf("advertiser")} placeholder="Brand name" /></div>
              <div><Label>Placement</Label><select data-testid="ad-placement" value={af.placement} onChange={saf("placement")} className="w-full mt-1 rounded-md border border-input bg-background px-3 py-2 text-sm"><option value="dashboard">Dashboard</option><option value="directory">Directory</option></select></div>
            </div>
            <div><Label>Image URL</Label><Input data-testid="ad-image" value={af.image} onChange={saf("image")} /></div>
            <div><Label>Link URL</Label><Input data-testid="ad-link" value={af.link_url} onChange={saf("link_url")} placeholder="https://..." /></div>
            <div className="flex items-center justify-between rounded-lg bg-muted/50 px-3 py-2"><Label>Active</Label><Switch data-testid="ad-active" checked={af.active} onCheckedChange={(v) => setAf({ ...af, active: v })} /></div>
            <div className="flex justify-end gap-2"><Button variant="ghost" onClick={() => setAdOpen(false)}>Cancel</Button><Button data-testid="save-ad" onClick={saveAd} className="rounded-full">Save</Button></div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Listing dialog */}
      <Dialog open={listOpen} onOpenChange={setListOpen}>
        <DialogContent className="max-w-lg rounded-2xl max-h-[88vh] overflow-y-auto scroll-thin">
          <DialogHeader><DialogTitle className="font-head">{editingList ? "Edit listing" : "Add listing"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Business name *</Label><Input data-testid="l-name" value={lf.name} onChange={slf("name")} /></div>
            <div><Label>Category</Label><select data-testid="l-category" value={lf.category} onChange={slf("category")} className="w-full mt-1 rounded-md border border-input bg-background px-3 py-2 text-sm">{cats.map((c) => <option key={c}>{c}</option>)}</select></div>
            <div><Label>Description</Label><Textarea data-testid="l-desc" value={lf.description} onChange={slf("description")} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Location</Label><Input data-testid="l-location" value={lf.location} onChange={slf("location")} /></div>
              <div><Label>Phone</Label><Input data-testid="l-phone" value={lf.phone} onChange={slf("phone")} /></div>
            </div>
            <div><Label>Website</Label><Input data-testid="l-website" value={lf.website} onChange={slf("website")} /></div>
            <div><Label>Image URL</Label><Input data-testid="l-image" value={lf.image} onChange={slf("image")} /></div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2"><Label>Featured</Label><Switch data-testid="l-featured" checked={lf.featured} onCheckedChange={(v) => setLf({ ...lf, featured: v })} /></div>
              <div className="flex items-center gap-2"><Label>Status</Label><select data-testid="l-status" value={lf.status} onChange={slf("status")} className="rounded-md border border-input bg-background px-3 py-1.5 text-sm"><option value="approved">Approved</option><option value="pending">Pending</option></select></div>
            </div>
            <div className="flex justify-end gap-2"><Button variant="ghost" onClick={() => setListOpen(false)}>Cancel</Button><Button data-testid="save-listing" onClick={saveList} className="rounded-full">Save</Button></div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
